<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import {
  TheUploader,
  VAlert,
  VButton,
  VLoadingSection,
  VModal,
  VSection,
  VTextField,
} from '@/modules/shared/components'
import VSelect from '@/modules/investing/components/v-select.vue'
import { rails_url } from '@/modules/shared/utils/rails'
import {
  SPV_EXIT_TYPE_OPTIONS,
  type ExitReadiness,
  type LossMarkingTarget,
  type SpvExitType,
} from '@/modules/investing/types'

const props = defineProps<{
  modalStore: { state: { active: boolean }; open: () => void; close: () => void }
  entityType: 'spv' | 'fund'
  // Called each time the slide-out opens. The slide-out owns the loading
  // state so the modal appears instantly on button click — see
  // venture360/tickets#1894.
  fetchExitReadiness: () => Promise<ExitReadiness>
  onSubmit: (payload: { exit_date: string; exit_type: SpvExitType; exit_documents: File[] }) => Promise<void>
}>()

const entityLabel = computed(() => (props.entityType === 'fund' ? 'Fund' : 'SPV'))

const route = useRoute()
const router = useRouter()

type Step = 'loading' | 'prompt' | 'confirm' | 'form'

const initialForm = () => ({
  exit_date: '',
  exit_type: 'Acquisition' as SpvExitType,
  exit_documents: [] as File[],
})

const step = ref<Step>('loading')
const lossMarkingTarget = ref<LossMarkingTarget | null>(null)
const form = ref(initialForm())
const submitting = ref(false)
const error = ref<string | null>(null)

const v$ = useVuelidate(
  {
    exit_date: { required },
    exit_type: { required },
  },
  form,
  { $lazy: true },
)

watch(
  () => props.modalStore.state.active,
  async (active) => {
    if (!active) {
      form.value = initialForm()
      submitting.value = false
      error.value = null
      v$.value.$reset()
      return
    }

    // Modal opens immediately in the loading state; we fetch readiness each
    // open because a distribution/loss recorded between opens can flip the
    // flow from Case A to Case B.
    step.value = 'loading'
    lossMarkingTarget.value = null
    try {
      const readiness = await props.fetchExitReadiness()
      lossMarkingTarget.value = readiness.loss_marking_target
      step.value = readiness.has_distribution_or_realized_loss ? 'confirm' : 'prompt'
    } catch {
      // If readiness fetch fails, fall back to Case B — the server guard is
      // authoritative and will reject on submit if preconditions aren't met.
      step.value = 'confirm'
    }
  },
)

const cancel = () => props.modalStore.close()
const proceedToForm = () => (step.value = 'form')
const backToConfirm = () => (step.value = 'confirm')

const recordDistribution = () => {
  const { slug, entity_id, entity_type } = route.params as { slug: string; entity_id: string; entity_type: string }
  props.modalStore.close()
  router.push({
    name: 'investing.disbursement.new',
    params: { slug, entity_type, entity_id },
  })
}

const canMarkAsTotalLoss = computed<boolean>(() => !!lossMarkingTarget.value)

// Loss-marking lives on the Rails-side company pages: equity investments
// use /companies/:id/valuations, loan-like investments (Convertible Note,
// Loan, Safe Note) use /companies/:id/terms/:term_sheet_id/history. The
// backend picks the right path; we just navigate.
const markAsTotalLoss = () => {
  const target = lossMarkingTarget.value
  if (!target) return
  const { slug } = route.params as { slug: string }
  props.modalStore.close()
  window.location.href = `${rails_url(slug)}${target.path}`
}

const submit = async () => {
  const valid = await v$.value.$validate()
  if (!valid || submitting.value) return

  submitting.value = true
  error.value = null
  try {
    await props.onSubmit({
      exit_date: form.value.exit_date,
      exit_type: form.value.exit_type,
      exit_documents: form.value.exit_documents,
    })
    props.modalStore.close()
  } catch (e: any) {
    error.value = e?.data?.error ?? e?.message ?? 'Failed to record exit'
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <VModal :modalStore="modalStore">
    <template #main>
      <div v-if="step === 'loading'" class="flex h-64 items-center justify-center">
        <VLoadingSection description="Checking exit requirements…" />
      </div>

      <VSection v-else-if="step === 'prompt'" label="Before you exit">
        <p class="text-sm leading-relaxed text-gray-600">
          Before exiting, the outcome of this {{ entityLabel }} needs to be captured. Is this a total loss investment,
          or do you need to record any distributions?
        </p>

        <VAlert type="info" class="mt-6">
          Exit is a historical, read-only state — changes to distributions or loss status can't be made afterwards
          without contacting Venture360 support. Record the outcome here first, then come back to complete the exit.
        </VAlert>

        <div class="mt-6 space-y-3">
          <button
            type="button"
            class="group block w-full rounded-lg border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-sky-300 hover:bg-sky-50/40"
            @click="recordDistribution"
          >
            <div class="font-medium text-gray-800 group-hover:text-sky-900">Record a distribution</div>
            <div class="mt-1 text-sm text-gray-500">
              Capture a cash or asset distribution returned to investors before closing out the {{ entityLabel }}.
            </div>
          </button>

          <button
            v-if="canMarkAsTotalLoss"
            type="button"
            class="group block w-full rounded-lg border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-sky-300 hover:bg-sky-50/40"
            @click="markAsTotalLoss"
          >
            <div class="font-medium text-gray-800 group-hover:text-sky-900">Mark as total loss</div>
            <div class="mt-1 text-sm text-gray-500">
              The investment in
              <span class="font-medium text-gray-700">{{ lossMarkingTarget?.company_name }}</span>
              was written off with no return to investors. This records a realized loss.
            </div>
          </button>
        </div>
      </VSection>

      <VSection v-else-if="step === 'confirm'" :label="`Exit this ${entityLabel}?`">
        <p class="text-sm leading-relaxed text-gray-600">
          Exiting marks this {{ entityLabel }} as a completed, historical record. Take a moment to confirm it's the
          right time.
        </p>

        <VAlert type="alert" class="mt-6">
          <strong class="font-semibold">After exit, these actions require Venture360 support:</strong>
          <ul class="ml-5 mt-2 list-disc space-y-1">
            <li>Recording additional distributions</li>
            <li>Marking investments as a total loss</li>
            <li>Changing the exit date or exit type</li>
          </ul>
          <p class="mt-3">Please make sure everything is recorded before you continue.</p>
        </VAlert>
      </VSection>

      <VSection v-else label="Exit details">
        <p class="text-sm leading-relaxed text-gray-600">
          Record when this {{ entityLabel }} exited and how the exit was realized. These details appear on the
          {{ entityLabel }}'s historical summary.
        </p>

        <form class="mt-6 space-y-6" @submit.prevent="submit">
          <VTextField
            v-model="form.exit_date"
            label="Exit date"
            type="date"
            property="exit_date"
            :required="true"
            :v$="v$"
          />

          <VSelect
            v-model="form.exit_type"
            label="Exit type"
            property="exit_type"
            :options="SPV_EXIT_TYPE_OPTIONS"
            :required="true"
            :v$="v$"
          />

          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700"
              >Exit documents <span class="font-normal text-gray-400">(optional)</span></label
            >
            <TheUploader :accept="[]" @chosen="(files: File[]) => (form.exit_documents = files)" />
          </div>

          <p v-if="error" class="text-sm text-red-600" role="alert">{{ error }}</p>
        </form>
      </VSection>
    </template>

    <template #footer>
      <div v-if="step === 'loading'" class="flex items-center justify-between gap-3">
        <VButton :click="cancel" size="lg" class="w-full">Cancel</VButton>
      </div>
      <div v-else-if="step === 'prompt'" class="flex items-center justify-between gap-3">
        <VButton :click="cancel" size="lg" class="w-full">Not now</VButton>
      </div>
      <div v-else-if="step === 'confirm'" class="flex items-center justify-between gap-3">
        <VButton :click="cancel" size="lg">Cancel</VButton>
        <VButton :click="proceedToForm" size="lg" variant="primary" class="w-full">Continue</VButton>
      </div>
      <div v-else class="flex items-center justify-between gap-3">
        <VButton :click="backToConfirm" :disabled="submitting" size="lg">Back</VButton>
        <VButton :click="submit" :loading="submitting" size="lg" variant="primary" class="w-full">Record Exit</VButton>
      </div>
    </template>
  </VModal>
</template>
